import Phaser from 'phaser';
import { Entity } from './Entity';
import { System } from './System';
import { Component } from './Component';

/**
 * ECS World - Manages all entities and systems
 * Core orchestrator for the game's entity-component-system architecture
 */
export class World {
  private scene: Phaser.Scene;
  private entities: Map<string, Entity> = new Map();
  private systems: System[] = [];
  private entitiesToDestroy: Set<string> = new Set();
  private nextEntityId = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Get the Phaser scene
   */
  public getScene(): Phaser.Scene {
    return this.scene;
  }

  /**
   * Create a new entity with a unique ID
   */
  public createEntity(id?: string): Entity {
    const entityId = id ?? `entity_${this.nextEntityId++}`;
    const entity = new Entity(entityId, this);
    this.entities.set(entityId, entity);
    return entity;
  }

  /**
   * Get an entity by ID
   */
  public getEntity(id: string): Entity | undefined {
    return this.entities.get(id);
  }

  /**
   * Get all entities
   */
  public getAllEntities(): Entity[] {
    return Array.from(this.entities.values());
  }

  /**
   * Mark an entity for destruction (will be removed at end of frame)
   */
  public destroyEntity(id: string): void {
    this.entitiesToDestroy.add(id);
  }

  /**
   * Query entities that have all specified component types
   */
  public query(componentTypes: string[]): Entity[] {
    return this.getAllEntities().filter((entity) =>
      componentTypes.every((type) => entity.hasComponent(type))
    );
  }

  /**
   * Query entities that have any of the specified component types
   */
  public queryAny(componentTypes: string[]): Entity[] {
    return this.getAllEntities().filter((entity) =>
      componentTypes.some((type) => entity.hasComponent(type))
    );
  }

  /**
   * Get all entities with a specific tag
   */
  public getEntitiesByTag(tag: string): Entity[] {
    return this.getAllEntities().filter((entity) => entity.hasTag(tag));
  }

  /**
   * Add a system to the world
   * Systems are processed in the order they are added
   */
  public addSystem(system: System): void {
    system.setWorld(this);
    this.systems.push(system);
    system.init();
  }

  /**
   * Remove a system from the world
   */
  public removeSystem(system: System): void {
    const index = this.systems.indexOf(system);
    if (index !== -1) {
      this.systems.splice(index, 1);
      system.destroy();
    }
  }

  /**
   * Get a system by its constructor
   */
  public getSystem<T extends System>(SystemClass: new (...args: unknown[]) => T): T | undefined {
    return this.systems.find((s) => s instanceof SystemClass) as T | undefined;
  }

  /**
   * Update all systems
   * Called every frame from the scene's update method
   */
  public update(delta: number): void {
    // Update all systems
    for (const system of this.systems) {
      if (system.enabled) {
        const entities = this.query(system.requiredComponents);
        system.update(entities, delta);
      }
    }

    // Clean up destroyed entities
    this.cleanupDestroyedEntities();
  }

  /**
   * Remove entities marked for destruction
   */
  private cleanupDestroyedEntities(): void {
    for (const id of this.entitiesToDestroy) {
      const entity = this.entities.get(id);
      if (entity) {
        // Notify systems of entity removal
        for (const system of this.systems) {
          system.onEntityRemoved(entity);
        }
        // Remove all components
        entity.removeAllComponents();
        // Remove from map
        this.entities.delete(id);
      }
    }
    this.entitiesToDestroy.clear();
  }

  /**
   * Notify systems when a component is added to an entity
   */
  public onComponentAdded(entity: Entity, component: Component): void {
    for (const system of this.systems) {
      if (system.requiredComponents.includes(component.type)) {
        system.onEntityAdded(entity);
      }
    }
  }

  /**
   * Notify systems when a component is removed from an entity
   */
  public onComponentRemoved(entity: Entity, component: Component): void {
    for (const system of this.systems) {
      if (system.requiredComponents.includes(component.type)) {
        system.onEntityRemoved(entity);
      }
    }
  }

  /**
   * Clean up the world
   */
  public destroy(): void {
    // Destroy all systems
    for (const system of this.systems) {
      system.destroy();
    }
    this.systems = [];

    // Destroy all entities
    for (const entity of this.entities.values()) {
      entity.removeAllComponents();
    }
    this.entities.clear();
  }
}
